import { Preferences } from "@capacitor/preferences";
import Cookies from "js-cookie";

export const STYTCH_SESSION_KEY = "stytch_session";
export const STYTCH_SESSION_JWT_KEY = "stytch_session_jwt";

export const SESSION_HYDRATION_DURATION_MINUTES = 30 * 24 * 60; // 30 days

export async function setStytchSessionTokens(
  sessionToken: string,
  sessionJwt: string,
): Promise<void> {
  await Promise.all([
    Preferences.set({ key: STYTCH_SESSION_KEY, value: sessionToken }),
    Preferences.set({ key: STYTCH_SESSION_JWT_KEY, value: sessionJwt }),
  ]);
}

export async function getStytchSessionTokens(): Promise<{
  sessionToken: string;
  sessionJwt: string;
} | null> {
  const [sessionJwt, sessionToken] = await Promise.all([
    getStytchSessionJwt(),
    getStytchSessionToken(),
  ]);

  if (!sessionToken || !sessionJwt) {
    return null;
  }

  return { sessionToken, sessionJwt };
}

export async function getStytchSessionJwt(): Promise<string | null> {
  const { value } = await Preferences.get({ key: STYTCH_SESSION_JWT_KEY });
  return value ?? null;
}

async function getStytchSessionToken(): Promise<string | null> {
  const { value } = await Preferences.get({ key: STYTCH_SESSION_KEY });
  return value ?? null;
}

export async function clearStytchSessionTokens(): Promise<void> {
  await Promise.all([
    Preferences.remove({ key: STYTCH_SESSION_KEY }),
    Preferences.remove({ key: STYTCH_SESSION_JWT_KEY }),
  ]);
}

/**
 * Reads tokens from capacitor preferences and restores the Stytch session cookies
 * **before** the StytchClient is instantiated, so the SDK picks them up on init.
 * Call this once at app startup, prior to `new StytchClient(...)`.
 */
export async function preInitializeStytchSessionCookies(): Promise<void> {
  if (Cookies.get(STYTCH_SESSION_KEY) && Cookies.get(STYTCH_SESSION_JWT_KEY)) {
    return;
  }

  const tokens = await getStytchSessionTokens();

  if (!tokens) {
    return;
  }

  restoreStytchSessionCookies(tokens.sessionToken, tokens.sessionJwt);
}

function restoreStytchSessionCookies(sessionToken: string, sessionJwt: string): void {
  const cookieAttributes: Cookies.CookieAttributes = {
    path: "/",
    sameSite: "lax",
    expires: new Date(Date.now() + SESSION_HYDRATION_DURATION_MINUTES * 60 * 1000),
  };

  Cookies.set(STYTCH_SESSION_KEY, sessionToken, cookieAttributes);
  Cookies.set(STYTCH_SESSION_JWT_KEY, sessionJwt, cookieAttributes);
}
